import { useState } from "react";
import { nriProblemData, nriWhatsappUrl } from "../../data/nriTaxData";
import { AlertTriangle, ChevronDown, ArrowRight } from "lucide-react";

const MistakeCard = ({ mistake, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border transition-all duration-300 rounded-sm overflow-hidden ${open ? "border-[#3f4816]/30 bg-[#302f2c]/50" : "border-[#3f4816]/10 bg-[#302f2c]/20 hover:border-[#3f4816]/25"}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 text-left" data-testid={`nri-mistake-${index}`}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="flex-shrink-0 w-6 h-6 rounded bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 text-xs font-black">✕</span>
          <span className="text-[#dfddd6] font-bold text-sm" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{mistake.title}</span>
        </div>
        <ChevronDown size={15} className={`text-[#888680] flex-shrink-0 ml-3 transition-transform duration-300 ${open ? "rotate-180 text-[#d9fb06]" : ""}`} />
      </button>
      <div className="overflow-hidden transition-all duration-300" style={{ maxHeight: open ? "200px" : "0", opacity: open ? 1 : 0 }}>
        <p className="px-5 pb-4 pt-0 pl-14 text-[#888680] text-xs leading-relaxed" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{mistake.body}</p>
      </div>
    </div>
  );
};

export const NriProblemSection = () => (
  <section id="nri-problem" className="relative py-28" style={{ background: "#1a1c1b" }}>
    <div className="absolute top-0 left-0 right-0 h-px bg-[#3f4816]/30" />
    <div className="max-w-[87.5rem] mx-auto px-10">

      {/* Heading — hero pattern */}
      <div className="mb-14">
        <span className="inline-block text-[#888680] text-xs font-medium uppercase tracking-[0.2em] mb-5" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
          {nriProblemData.sectionLabel}
        </span>
        <p className="text-[#888680]/60 font-semibold uppercase mb-1" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(0.85rem, 0.7rem + 0.8vw, 1.1rem)", letterSpacing: "-0.01em" }}>
          You have a consultant. But is it
        </p>
        <h2 className="font-black uppercase leading-tight" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(1.8rem, 1.4rem + 2.5vw, 3.5rem)", letterSpacing: "-0.03em" }}>
          <span className="text-[#d9fb06]">The right one </span>
          <span className="text-[#888680]/50">for someone</span>
          <br />
          <span className="text-[#dfddd6]">living </span>
          <span style={{ borderBottom: "3px solid #d9fb06", paddingBottom: "2px" }} className="text-[#dfddd6]">outside India?</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Left — one punchy line + accordions */}
        <div>
          <p className="text-[#888680] text-sm mb-6 border-l-2 border-[#3f4816]/40 pl-4" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
            They're not bad at their job. They've just{" "}
            <span className="text-[#dfddd6] font-semibold">never filed for someone living abroad.</span>
            {" "}Here's what that costs you:
          </p>
          <div className="flex flex-col gap-2">
            {nriProblemData.mistakes.map((m, i) => <MistakeCard key={i} mistake={m} index={i} />)}
          </div>
        </div>

        {/* Right — cost table + callout */}
        <div className="flex flex-col gap-5">
          <div className="bg-[#302f2c]/40 border border-[#3f4816]/15 rounded-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-[#3f4816]/20 bg-[#252422]/60">
              <p className="text-[#888680] text-[10px] font-black uppercase tracking-widest" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>Hidden Cost Breakdown</p>
            </div>
            {nriProblemData.costTable.map((row, i) => (
              <div key={i} className={`px-5 py-4 ${i < nriProblemData.costTable.length - 1 ? "border-b border-[#3f4816]/10" : ""}`}>
                <p className="text-[#888680]/50 text-[10px] mb-2 leading-snug uppercase tracking-wide" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{row.mistake}</p>
                <p className="text-[#d9fb06] font-black leading-none" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(1.4rem, 1.1rem + 1.2vw, 2rem)", letterSpacing: "-0.03em" }}>{row.costAmount}</p>
                <p className="text-[#888680]/60 text-[11px] mt-1" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{row.costDesc}</p>
              </div>
            ))}
          </div>

          {/* Callout */}
          <div className="relative bg-[#d9fb06]/5 border border-[#d9fb06]/25 p-5 rounded-sm overflow-hidden">
            <div className="absolute right-2 top-0 text-[#d9fb06]/5 font-black leading-none select-none" style={{ fontSize: "6rem", fontFamily: "'Inter', Arial, sans-serif" }}>₹</div>
            <div className="relative flex items-start gap-3">
              <AlertTriangle size={16} className="text-[#d9fb06] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[#d9fb06] font-black mb-0.5" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(1.2rem, 1rem + 0.8vw, 1.6rem)", letterSpacing: "-0.02em" }}>₹15–50 lakh</p>
                <p className="text-[#dfddd6] text-sm font-semibold mb-0.5" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>total hidden cost. 3–5 years.</p>
                <p className="text-[#888680] text-xs" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>Paying ₹4,000 for a filing.{" "}<span className="text-[#dfddd6] font-semibold">Risking 100×.</span></p>
              </div>
            </div>
          </div>

        <a href="#nri-bonuses"
          onClick={(e) => { e.preventDefault(); document.querySelector("#nri-bonuses")?.scrollIntoView({ behavior: "smooth" }); }}
          data-testid="nri-problem-cta"
          className="group inline-flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] font-bold text-xs px-6 py-3.5 rounded-full transition-all duration-300 hover:scale-[1.02] hover:opacity-90 uppercase tracking-tight self-start"
          style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
          Get My NRI Tax Report + Filing
          <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5" />
        </a>
        </div>
      </div>
    </div>
  </section>
);
